import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { useAppSettings } from '../context/AppSettingsContext';
import { confirm } from '../components/ui/confirm-dialog';
import apiClient from '../services/api';

const PROTECTED_ROLE = 'Super Admin';

export default function StaffDetail() {
  const { formatDate } = useAppSettings();
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const { data: pJson } = await apiClient.get(`/users/user/${staffId}/getUserDetails/`);
      if (pJson.success) {
        setProfile(pJson.data);
        setFormData({
          salutation: pJson.data.salutation || '',
          first_name: pJson.data.first_name || '',
          last_name: pJson.data.last_name || '',
          email: pJson.data.email || '',
          username: pJson.data.username || '',
          phone: pJson.data.phone || '',
          employee_id: pJson.data.employee_id || '',
          hire_date: pJson.data.hire_date ? pJson.data.hire_date.split('T')[0] : '',
          employment_status: pJson.data.employment_status || 'ACTIVE',
          shift: pJson.data.shift || 'FULL_TIME',
          staff_type_id: pJson.data.staff_type_id || 1,
          restaurant_id: pJson.data.restaurant_id || 1,
          // Staff created before POS access existed come back without the field; they could sign in, so keep them able to.
          can_login_pos: pJson.data.can_login_pos !== false,
          // Never pre-filled: the API only says whether a password exists, and blank means "leave it alone".
          password: ''
        });
      } else {
        setError(pJson.error || 'Failed to load staff details');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load staff details');
    }
  }, [staffId]);

  useEffect(()=>{ load(); },[load]);

  // Open directly in edit mode when arriving via ?edit=1, ignored for protected system users.
  useEffect(() => {
    if (profile && searchParams.get('edit') === '1') {
      if (profile.staff_type !== PROTECTED_ROLE) setEditMode(true);
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, [profile, searchParams, setSearchParams]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const password = (formData.password || '').trim();
    // POS access is worth nothing without a password; catching it here saves a round trip and keeps the form up.
    if (formData.can_login_pos && !profile?.has_password && !password) {
      setError('Set a password before allowing this person to sign in on the POS.');
      return;
    }
    try {
      // An untouched password field is left out of the payload entirely, rather than sent empty and clearing it.
      const payload = { ...formData };
      if (password) payload.password = password; else delete payload.password;
      const { data } = await apiClient.put(`/users/user/${staffId}/update/`, payload);
      if (data.success) {
        setSuccess(password ? 'User updated and password set' : 'User updated successfully');
        setEditMode(false);
        setShowPassword(false);
        load();
      } else {
        setError(data.error || 'Failed to update user');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({ description: `Are you sure you want to delete this user?`, confirmText: 'Delete user', tone: 'destructive' }))) return;
    setError('');
    try {
      const { data } = await apiClient.delete(`/users/user/${staffId}/delete/`);
      if (data.success) {
        navigate('/staff-management');
      } else {
        setError(data.error || 'Failed to delete user');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const fmt = (iso) => iso ? formatDate(iso) : '-';

  return (
    <div className="w-full p-6">
      {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}
      {success && <Alert className="mb-4 bg-green-100 text-green-800">{success}</Alert>}
      
      <div className="mb-4">
        <Button onClick={() => navigate('/staff-management')} variant="outline">
          ← Return to Staff Members
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Staff Details
            {profile?.staff_type === PROTECTED_ROLE && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                <ShieldCheck size={10} /> Protected
              </span>
            )}
          </CardTitle>
          {!editMode && (
            <div className="flex gap-2">
              <Button onClick={() => navigate(`/staff/${staffId}/sessions`)} variant="outline">
                View Sessions
              </Button>
              {profile?.staff_type === PROTECTED_ROLE ? (
                <span className="text-xs italic text-slate-500 self-center">
                  System user — cannot be edited or deleted
                </span>
              ) : (
                <>
                  <Button onClick={() => setEditMode(true)}>Edit</Button>
                  <Button variant="destructive" onClick={handleDelete}>Delete</Button>
                </>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!profile ? (<p>Loading...</p>) : editMode ? (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <select
                    name="salutation"
                    value={formData.salutation || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded"
                  >
                    {[['', 'None'], ['MR', 'Mr'], ['MRS', 'Mrs'], ['MS', 'Ms'],
                      ['MISS', 'Miss'], ['DR', 'Dr'], ['PROF', 'Prof']].map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Username *</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Employee ID</label>
                  <input
                    type="text"
                    name="employee_id"
                    value={formData.employee_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Hire Date</label>
                  <input
                    type="date"
                    name="hire_date"
                    value={formData.hire_date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Employment Status</label>
                  <select
                    name="employment_status"
                    value={formData.employment_status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="ON_LEAVE">On Leave</option>
                    <option value="TERMINATED">Terminated</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Shift</label>
                  <select
                    name="shift"
                    value={formData.shift}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="FULL_TIME">Full Time</option>
                    <option value="PART_TIME">Part Time</option>
                    <option value="MORNING">Morning</option>
                    <option value="EVENING">Evening</option>
                    <option value="NIGHT">Night</option>
                  </select>
                </div>
              </div>

              {/* POS access is the one part of this form that changes what the person can DO, so it sits with its password. */}
              <div className="rounded border border-slate-200 p-4 space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="can_login_pos"
                    checked={!!formData.can_login_pos}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-medium">Can sign in on the POS</span>
                    <span className="block text-xs text-slate-500">
                      Covers the Windows POS and the Android app. The back-office
                      web app is not affected by this switch — use Employment
                      Status or the account's Active flag for that.
                    </span>
                  </span>
                </label>

                {formData.can_login_pos && (
                  <div className="max-w-sm">
                    <label className="block text-sm font-medium mb-1">
                      {profile.has_password ? 'Reset password' : (
                        <>Set password <span className="text-rose-500">*</span></>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password || ''}
                        onChange={handleInputChange}
                        autoComplete="new-password"
                        placeholder={profile.has_password
                          ? 'Leave blank to keep the current one'
                          : 'At least 6 characters'}
                        className="w-full px-3 py-2 border rounded pr-9"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-800"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {profile.has_password
                        ? 'A password is already set — type a new one only to replace it.'
                        : 'This person has no password yet, so they cannot sign in until you set one.'}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit">Update User</Button>
                <Button type="button" onClick={() => { setEditMode(false); setShowPassword(false); }} variant="outline">Cancel</Button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><strong>Name:</strong> {profile.full_name || `${profile.first_name} ${profile.last_name}`}</div>
              <div><strong>Email:</strong> {profile.email}</div>
              <div><strong>Role:</strong> {profile.staff_type || '-'}</div>
              <div><strong>Phone:</strong> {profile.phone || '-'}</div>
              <div><strong>Status:</strong> {profile.employment_status || '-'}</div>
              <div><strong>Employee ID:</strong> {profile.employee_id || '-'}</div>
              <div><strong>Restaurant:</strong> {profile.restaurant_name || '-'}</div>
              <div><strong>Username:</strong> {profile.username || '-'}</div>
              <div><strong>Hire Date:</strong> {fmt(profile.hire_date)}</div>
              <div><strong>Shift:</strong> {profile.shift || '-'}</div>
              <div>
                <strong>POS access:</strong>{' '}
                {profile.can_login_pos === false ? 'Not allowed' : 'Allowed'}
                {profile.can_login_pos !== false && !profile.has_password && (
                  <span className="text-amber-700"> — no password set</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
